import { useState } from 'react';
import {
  Button,
  CalendarMonthIcon,
  ChevronDownIcon,
  DatePicker,
  Divider,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@billda/design-system';
import { format } from 'date-fns';

interface DateRangeControlProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

const MS_DAY = 86_400_000;

const PRESETS = [
  { label: 'Last 2 weeks', days: 13 },
  { label: 'Last 30 days', days: 29 },
  { label: 'Last 90 days', days: 89 },
];

function rangeLabel(from: string, to: string): string {
  if (!from && !to) {
    return 'All dates';
  }

  if (!from) {
    return `Until ${format(new Date(to), 'd MMM yyyy')}`;
  }

  if (!to) {
    return `From ${format(new Date(from), 'd MMM yyyy')}`;
  }

  const f = new Date(from);
  const t = new Date(to);
  const sameYear = f.getFullYear() === t.getFullYear();
  return `${format(f, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${format(t, 'd MMM yyyy')}`;
}

export function DateRangeControl({
  from,
  to,
  onChange,
}: DateRangeControlProps) {
  const [open, setOpen] = useState(false);

  const applyPreset = (days: number) => {
    const t = new Date();
    const f = new Date(Date.now() - days * MS_DAY);
    onChange(f.toISOString(), t.toISOString());
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-content-secondary"
          data-testid="activity-date-range"
        >
          <CalendarMonthIcon aria-hidden="true" className="size-4" />
          {rangeLabel(from, to)}
          <ChevronDownIcon aria-hidden="true" className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-75">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant={!from && !to ? 'neutral' : 'outline'}
              onClick={() => {
                onChange('', '');
              }}
            >
              All dates
            </Button>
            {PRESETS.map(p => (
              <Button
                key={p.label}
                size="sm"
                variant="outline"
                onClick={() => {
                  applyPreset(p.days);
                }}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Divider />
          <DatePicker
            label="From"
            value={from ? new Date(from) : null}
            onChange={d => {
              if (d) {
                onChange(d.toISOString(), to);
              }
            }}
          />
          <DatePicker
            label="To"
            value={to ? new Date(to) : null}
            onChange={d => {
              if (d) {
                onChange(from, d.toISOString());
              }
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
